/**
 * <newsletter-popup> — native <dialog> email capture.
 * Triggers: exit_intent (desktop mouseleave at top; scroll on touch), scroll, timer.
 * Every trigger waits at least 8 s. State lives in localStorage `hurtliche:newsletter`:
 *   { subscribed: boolean, dismissals: number, last: epoch ms }
 * Never opens: in the theme editor unless the section is selected, for subscribed
 * visitors, after max dismissals within the suppress window, on cart / checkout /
 * policy URLs, or when a utm_* param points at a cart / checkout recovery flow.
 */
if (!customElements.get('newsletter-popup')) {
  const STORAGE_KEY = 'hurtliche:newsletter';
  const MIN_DELAY_SECONDS = 8;
  const DAY_MS = 86400000;

  customElements.define(
    'newsletter-popup',
    class NewsletterPopup extends HTMLElement {
      connectedCallback() {
        this.dialog = this.querySelector('dialog');
        if (!this.dialog || typeof this.dialog.showModal !== 'function') return;

        this.sectionId = this.dataset.sectionId;
        this.designMode = this.hasAttribute('data-design-mode');
        this.trigger = this.dataset.trigger || 'timer';
        this.delay = Math.max(MIN_DELAY_SECONDS, parseInt(this.dataset.delay, 10) || MIN_DELAY_SECONDS) * 1000;
        this.scrollPercent = Math.min(100, Math.max(0, parseInt(this.dataset.scrollPercent, 10) || 25));
        this.maxDismissals = Math.max(1, parseInt(this.dataset.maxDismissals, 10) || 2);
        this.suppressDays = Math.max(1, parseInt(this.dataset.suppressDays, 10) || 30);
        this.subscribed = false;
        this.opened = false;
        this.abort = new AbortController();
        const { signal } = this.abort;

        this.querySelectorAll('[data-close]').forEach((el) =>
          el.addEventListener('click', () => this.close(), { signal })
        );
        this.dialog.addEventListener('click', (e) => { if (e.target === this.dialog) this.close(); }, { signal });
        this.dialog.addEventListener('close', () => this.onClose(), { signal });
        document.addEventListener('newsletter:subscribed', () => this.onSubscribed(), { signal });

        if (this.designMode) {
          this.bindEditor(signal);
          return;
        }

        if (this.hasAttribute('data-disabled')) return;
        if (this.shouldSuppress()) return;
        this.arm(signal);
      }

      disconnectedCallback() {
        this.abort?.abort();
        clearTimeout(this.timer);
        clearTimeout(this.retryTimer);
      }

      /* ---------- Storage ---------- */
      readState() {
        try {
          const raw = window.localStorage.getItem(STORAGE_KEY);
          const state = raw ? JSON.parse(raw) : {};
          return { subscribed: !!state.subscribed, dismissals: state.dismissals || 0, last: state.last || 0 };
        } catch (e) {
          return { subscribed: false, dismissals: 0, last: 0 };
        }
      }

      writeState(patch) {
        try {
          window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...this.readState(), ...patch }));
        } catch (e) {
          /* private mode: fall through, popup simply re-arms next visit */
        }
      }

      shouldSuppress() {
        const params = new URLSearchParams(window.location.search);
        if (params.get('customer_posted') === 'true' || params.get('contact_posted') === 'true') {
          this.writeState({ subscribed: true });
          return true;
        }
        for (const [key, value] of params) {
          if (key.startsWith('utm_') && /checkout|cart|abandon/i.test(value)) return true;
        }
        if (/^\/(cart|checkout|policies)(\/|$)/.test(window.location.pathname) || /\/checkouts?\//.test(window.location.pathname)) {
          return true;
        }

        const state = this.readState();
        if (state.subscribed) return true;
        if (state.dismissals >= this.maxDismissals) {
          if (Date.now() - state.last < this.suppressDays * DAY_MS) return true;
          this.writeState({ dismissals: 0 });
        }
        return false;
      }

      /* ---------- Triggers ---------- */
      arm(signal) {
        const touch = window.matchMedia('(hover: none), (pointer: coarse)').matches;
        let mode = this.trigger;
        if (mode === 'exit_intent' && touch) mode = 'scroll';

        this.delayElapsed = false;
        this.timer = setTimeout(() => {
          this.delayElapsed = true;
          if (mode === 'timer') this.tryOpen();
          if (mode === 'scroll') this.checkScroll();
        }, this.delay);

        if (mode === 'scroll') {
          window.addEventListener('scroll', () => this.checkScroll(), { passive: true, signal });
        }

        if (mode === 'exit_intent') {
          document.documentElement.addEventListener(
            'mouseleave',
            (e) => {
              if (this.delayElapsed && e.clientY <= 0) this.tryOpen();
            },
            { signal }
          );
        }
      }

      checkScroll() {
        if (!this.delayElapsed || this.opened) return;
        const max = document.documentElement.scrollHeight - window.innerHeight;
        const percent = max <= 0 ? 100 : (window.scrollY / max) * 100;
        if (percent >= this.scrollPercent) this.tryOpen();
      }

      /* ---------- Open / close ---------- */
      tryOpen(attempt = 0) {
        if (this.opened || this.dialog.open) return;
        // Another drawer / modal is open: wait rather than stack.
        const busy = document.body.classList.contains('overflow-hidden') || document.querySelector('dialog[open]');
        if (busy && !this.designMode) {
          if (attempt < 6) this.retryTimer = setTimeout(() => this.tryOpen(attempt + 1), 5000);
          return;
        }
        this.open();
      }

      open() {
        if (this.dialog.open) return;
        this.opened = true;
        this.dialog.showModal();
        document.body.classList.add('overflow-hidden');
        const focusTarget = this.dialog.querySelector('input:not([type=hidden]):not([type=checkbox])') || this.dialog.querySelector('[data-close]');
        focusTarget?.focus({ preventScroll: true });
      }

      close() {
        if (this.dialog.open) this.dialog.close();
      }

      onClose() {
        document.body.classList.remove('overflow-hidden');
        if (this.designMode || this.subscribed) return;
        const state = this.readState();
        this.writeState({ dismissals: state.dismissals + 1, last: Date.now() });
      }

      onSubscribed() {
        this.subscribed = true;
        this.writeState({ subscribed: true });
        clearTimeout(this.timer);
        clearTimeout(this.retryTimer);
        // Subscribed elsewhere on the page (footer) before the popup opened: never open.
        if (!this.dialog.open) this.opened = true;
      }

      /* ---------- Theme editor ---------- */
      bindEditor(signal) {
        document.addEventListener(
          'shopify:section:select',
          (e) => {
            if (e.detail?.sectionId === this.sectionId) this.open();
          },
          { signal }
        );
        document.addEventListener(
          'shopify:section:deselect',
          (e) => {
            if (e.detail?.sectionId === this.sectionId) this.close();
          },
          { signal }
        );
      }
    }
  );
}
